/**
 * The books the queue metrics read, by identity, as one string
 * (floor-selection-churn, 2026-10-22).
 *
 * The Position Tracker's per-order queue metrics (`queueMetricsByOrderId`)
 * read only the open orders' books, yet rebuilt on every 4 Hz market tick:
 * every admitted market frame bumps the store version, and during an hc1
 * floor replay (~1,200 rows/s for 7–45 s; 28 % of a measured PM window) that
 * is the rebuild running at its cap for the whole replay — the "flicker" a
 * tracker user sees — while none of those frames carried a book.
 *
 * A market frame never changes a book object; an orderbook ingest replaces
 * it (`_orderbooks.set(ticker, book)` with a fresh object), a withdrawal
 * drops it, and the display snapshot's retention state moves with them. So
 * the books' IDENTITIES, plus the display snapshot's retention fields, are
 * the whole dependency: the tracker computes this stamp at its 4 Hz clock
 * (one lookup per distinct ticker — trivial) and rebuilds the metrics only
 * when the stamp or the orders moved.
 */
import type { HydraOrderbook } from '@/clients/HydraClient';
import type { BookDisplaySnapshot } from '@/stores/marketStore';

const _ident = new WeakMap<object, number>();
let _nextIdent = 1;

/** A stable small integer per object identity; 0 for null / undefined. */
export function identityOf(o: object | null | undefined): number {
  if (o == null) return 0;
  let id = _ident.get(o);
  if (id == null) { id = _nextIdent++; _ident.set(o, id); }
  return id;
}

export function queueBookStamp(
  orders: ReadonlyArray<{ ticker: string }>,
  getRaw: (ticker: string) => HydraOrderbook | null,
  getDisplay: (ticker: string) => BookDisplaySnapshot,
): string {
  const parts: string[] = [];
  const seen = new Set<string>();
  for (const o of orders) {
    if (seen.has(o.ticker)) continue;
    seen.add(o.ticker);
    const d = getDisplay(o.ticker);
    parts.push(`${o.ticker}:${identityOf(getRaw(o.ticker))}:${identityOf(d.book)}:${d.retained ? 1 : 0}:${d.retentionExpiresAtMonoMs ?? ''}:${identityOf(d.continuity)}`);
  }
  return parts.join('|');
}
